import React, { useCallback, useState } from "react";
import { CenterModal } from "../../CenterModal";
import { SubscriptionModal } from "../../common/Subscription/SubscriptionModal";
import { CloseIcon, SuccessIcon } from "../../../assets/icons";
import PadLock from "../../../assets/icons/PadLock";
import SelectedMarkIcon from "../../../assets/icons/SelectedIcon";
import { CustomerT } from "../../../types";
import { editCustomer } from "../../../backend-services";
import ButtonCTA from "../ButtonCTA";
import { useSubscription } from "../../../context/SubscriptionProvider";
import PhoneInputComponent from "../../common/PhoneInputComponent";
import { IUpdateProfile } from "../../../@types";
import { useCompanyStore } from "../../../state/useCompanyStore";

interface ShareOptionProps {
  main: string;
  enabled: boolean;
  onToggle?: (value: boolean) => void;
  hasPremium?: boolean;
  className?: string;
  title: string;
  warningText?: string;
  customerId?: string;
  label: string;
  placeholder: string;
  modalTitle?: string;
  isLoadingState?: (value: boolean) => void;
}
interface CompanyUpdateProps {
  main: string;
  onToggle?: (value: boolean) => void;
  className?: string;
  warningText?: string;
  customerId?: string;
  label: string;
  placeholder: string;
  isPhone?: boolean;
  isLoadingState?: (value: boolean) => void;
}

export const ShareOption: React.FC<ShareOptionProps> = ({
  main,
  enabled,
  onToggle,
  hasPremium = false,
  className = "",
  title,
  warningText,
  customerId = "",
  label,
  placeholder,
  isLoadingState,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setIsSucess] = useState(false);
  const [errorText, setErrorText] = useState("");

  const handleEditCustomer = useCallback(
    async (customer: Partial<CustomerT>) => {
      isLoadingState?.(true);
      const { email, phone } = customer;

      // Basic validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\+?\d{7,15}$/;

      if (email && !emailRegex.test(email)) {
        setErrorText("Invalid email format");
        setTimeout(() => {
          setErrorText("");
          setInputValue(main || "");
        }, 3000);
        return;
      }

      if (phone && !phoneRegex.test(phone)) {
        setErrorText("Invalid phone number format");
        setTimeout(() => {
          setErrorText("");
          setInputValue(main || "");
        }, 3000);
        return;
      }
      setIsLoading(true);

      try {
        const data = await editCustomer(customerId, customer);
        setIsLoading(false);
        setIsSucess(true);
        setTimeout(() => {
          setIsSucess(false);
          isLoadingState?.(false);
        }, 2000);
      } catch (e) {
        console.error("API Error:", e);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );
  const [showWarning, setShowWarning] = useState(false);
  const [inputValue, setInputValue] = useState(main || "");

  const handleClick = () => {
    if (hasPremium) {
      onToggle?.(!enabled);
    } else {
      setShowWarning(!showWarning);
    }
  };

  const clearInput = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevents toggling the main option
    setInputValue("");
  };

  //   if (!main && !hasPremium && !enabled) return null;

  return (
    <>
      <div
        className={`p-2 rounded-md mb-2 cursor-pointer ${
          enabled ? "bg-[#F9FAFB] border-[#EAECF0] border" : "bg-white"
        } ${className}`}
      >
        <div
          onClick={handleClick}
          className="flex justify-between items-center mb-2"
        >
          <span
            className={`text-[18px] ${
              hasPremium ? "text-black" : "text-[#D0D5DD]"
            } flex items-center gap-2 font-medium`}
          >
            {title} {!hasPremium && <PadLock />}
          </span>
          {enabled ? (
            <SelectedMarkIcon />
          ) : (
            <span className="w-5 h-5 rounded-full border-2 border-gray-300"></span>
          )}
        </div>

        {enabled && (
          <div>
            <div className="mt-[0.4em] mb-2 relative">
              {label == "email" ? (
                <input
                  type="text"
                  disabled={!hasPremium}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className={`w-full mt-2 border border-gray-300 rounded-md flex items-center px-3 py-2 pr-8 h-[48px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!hasPremium && "bg-gray-100"}`}
                  placeholder={placeholder}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={() => {
                    if (inputValue.length > 0) {
                      handleEditCustomer({ [label.toLowerCase()]: inputValue });
                    }
                  }}
                />
              ) : (
                <PhoneInputComponent
                  initialPhoneNumber={inputValue}
                  onPhoneNumberChange={(e) => setInputValue(e)}
                  value={inputValue}
                  inputClass="onboarding"
                  containerClass="onboarding"
                  onBlurUpdate={(data) => {
                    if (inputValue.length > 0) {
                      handleEditCustomer({ [label.toLowerCase()]: data });
                    }
                  }}
                />
              )}
              {inputValue && (
                <button
                  disabled={!hasPremium}
                  onClick={clearInput}
                  className="absolute right-2 top-[0.81em] text-gray-400 hover:text-gray-600"
                >
                  {!hasPremium ? <PadLock height={20} width={20} /> : <></>}
                </button>
              )}
            </div>

            {errorText && (
              <p className="text-[14px] ml-2 text-red-500 text-sm font-medium">
                {errorText}
              </p>
            )}
            <>
              {isLoading ? (
                <p className="text-[14px] ml-2 text-[#044E97] text-sm font-medium">
                  Updating...
                </p>
              ) : (
                <>
                  {success && (
                    <p className="text-[14px] ml-2 gap-1 items-center text-[#039855] flex text-sm font-medium ">
                      <SuccessIcon /> Update complete
                    </p>
                  )}
                </>
              )}
            </>
          </div>
        )}
        {!hasPremium && showWarning && (
          <p className="text-[#344054] text-sm font-medium">{warningText}</p>
        )}
      </div>
    </>
  );
};
export const CompanyUpdateInput: React.FC<CompanyUpdateProps> = ({
  main,
  className = "",
  warningText,
  label,
  placeholder,
  isLoadingState,
  isPhone,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setIsSucess] = useState(false);
  const [errorText, setErrorText] = useState("");

  const update = async (updatedFields: Partial<IUpdateProfile>) => {
    await useCompanyStore.getState().updateCompanyProfile(updatedFields);
  };

  const handleUpdateCompany = useCallback(
    async (company: Partial<IUpdateProfile>) => {
      isLoadingState?.(true);
      setIsLoading(true);
      const { phone } = company;

      // Basic validation
      const phoneRegex = /^\+?\d{7,15}$/;

      if (phone && !phoneRegex.test(phone)) {
        setErrorText("Invalid phone number format");
        setTimeout(() => {
          setErrorText("");
          setInputValue(main || "");
        }, 3000);
        return;
      }

      try {
        await update(company);
        setIsLoading(false);
        setIsSucess(true);
        setTimeout(() => {
          setIsSucess(false);
          isLoadingState?.(false);
        }, 2000);
      } catch (e) {
        console.error("API Error:", e);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );
  const [showWarning, setShowWarning] = useState(false);
  const [inputValue, setInputValue] = useState(main || "");

  const clearInput = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue("");
  };

  return (
    <>
      <div>
        <div className="mb-2 relative">
          {!isPhone ? (
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className={`w-full border border-gray-300 rounded-md flex items-center px-3 py-2 pr-8 h-[48px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder={placeholder}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => {
                if (inputValue.length > 0) {
                  handleUpdateCompany({
                    [label]: inputValue,
                  });
                }
              }}
            />
          ) : (
            <PhoneInputComponent
              initialPhoneNumber={inputValue}
              onPhoneNumberChange={(e) => setInputValue(e)}
              value={inputValue}
              inputClass="onboarding"
              containerClass="onboarding"
              onBlurUpdate={(data) => {
                if (inputValue.length > 0) {
                  handleUpdateCompany({ [label.toLowerCase()]: data });
                }
              }}
            />
          )}
        </div>

        {errorText && (
          <p className="text-[14px] ml-2 text-red-500 text-sm font-medium">
            {errorText}
          </p>
        )}
        <>
          {isLoading ? (
            <p className="text-[14px] ml-2 text-[#044E97] text-sm font-medium">
              Saving...
            </p>
          ) : (
            <>
              {success && (
                <p className="text-[14px] ml-2 gap-1 items-center text-[#039855] flex text-sm font-medium ">
                  <SuccessIcon /> Update complete
                </p>
              )}
            </>
          )}
        </>
      </div>
    </>
  );
};

const ReusableSendModal = ({
  modalTitle = "Send Reminder",
  handleSubmit = () => {},
  customer = {} as CustomerT,
}) => {
  const { hasSubscription } = useSubscription();
  const [medium, setMedium] = useState({
    sms: false,
    whatsapp: false,
    email: true,
  });
  const handleMediumChanges = (option: string) => {
    setMedium({
      sms: option === "sms",
      whatsapp: option === "whatsapp",
      email: option === "email",
    });
  };
  return (
    <SubscriptionModal>
      <section className="bg-white w-[500px] mx-5 rounded-xl md:p-6 p-4">
        {/* Title and close button */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-semibold text-[20px]">{modalTitle}</h1>
          <button>
            <CloseIcon />
          </button>
        </div>

        {/* Options */}
        <ShareOption
          enabled={medium.email}
          hasPremium={true}
          main={""}
          className=""
          onToggle={() => handleMediumChanges("email")}
          title={"Share Via Email"}
          warningText={"Upgrade your plan to access this feature"}
          label="email"
          placeholder="Enter email address"
        />
        <ShareOption
          enabled={medium.sms}
          hasPremium={hasSubscription()}
          main={customer?.phone}
          className=""
          onToggle={() => handleMediumChanges("sms")}
          title={"Share Via SMS"}
          warningText={"Upgrade your plan to access this feature"}
          label="phone"
          placeholder="Enter phone number"
        />
        <ShareOption
          enabled={medium.whatsapp}
          hasPremium={hasSubscription()}
          main={customer?.phone}
          className=""
          onToggle={() => handleMediumChanges("whatsapp")}
          title={"Share Via WhatsApp"}
          warningText={"Upgrade your plan to access this feature"}
          label="phone"
          placeholder="Enter phone number"
        />

        <ButtonCTA className="w-full" disabled onClick={handleSubmit}>
          Send Reminder
        </ButtonCTA>
      </section>
    </SubscriptionModal>
  );
};

export default ReusableSendModal;
